/**
 * SOLID Review
 * S — Strong. One concern: what the screen needs from the table that only the game's rules can say.
 * O — Good. A new derived value is one field here and one in its schema.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. One function: the table in, the derived values out.
 * D — Strong. The Domain's own rules (dice ranking, challenger rotation, the awaited player) and its
 *     schema types. No store, no port.
 */
import { GamePhase } from '../../uml-components/primitives';
import { VALID_SCORES, MEYER_RANK, scoreLabel, scoreToDice } from '../../dice/DiceRankingService';
import { getChallengerId } from '../../game/ChallengerSelection';
import { resolveBlockingSuspendedOpponentId } from '../../game/BlockingSuspension';
import type { IStateClientTable, IEntityClientTableDerived, IEntityClientClaimOption } from '../../ports/schemas/ClientTable';

/**
 * THE TABLE'S DERIVED VALUES (frontend strict-ports plan, FE1, 2026-10-03). Each is the expression a
 * component computed inline, moved here unchanged:
 *
 *   - `challengerId`: AppRoot's `activeChallengerId ?? getChallengerId(…)`. ControlPanel computed the same
 *     thing as `getChallengerId(…)` with `activeChallengerId ?? challengerId` at its single use.
 *   - `blockingOpponentId`: AppRoot's `resolveBlockingSuspendedOpponentId(…)` over that challenger.
 *   - the claim lists: ControlPanel's two pickers. The blind picker lists every claim over the floor. The
 *     open picker splits them at the rolled score, but only when there is a floor to beat; the first claim
 *     of a game may be anything.
 *   - `cupAction`: GameSceneCore's `handleCupTap` dispatch, including its `!activeRoundId` bail-out. The
 *     scenes' `canTap` omitted that check, so a cup without a round glowed and invited a tap that did
 *     nothing; they read this now (deviation, flagged in §28 for FE1).
 */
export function deriveTable(table: IStateClientTable): IEntityClientTableDerived {
  const me = table.localPlayerId;
  const challengerId = table.activeChallengerId
    ?? getChallengerId(table.room?.players ?? [], table.activeRollerId, table.playerLives, table.suspendedPlayerIds);
  const isActiveRoller = me !== null && me === table.activeRollerId;
  const rolled = table.lastDiceRoll === null ? null : table.lastDiceRoll.score;
  const option = (score: number): IEntityClientClaimOption => ({ score, label: scoreLabel(score), isRolled: score === rolled });
  // A claim must strictly BEAT the floor — matching it is not a legal claim.
  const legalClaims = VALID_SCORES.filter((s) => s > table.minimumClaimScore).map(option);
  // With the dice seen and a floor to beat: below the rolled score is a bluff, the rest is honest.
  const tacticalClaims = rolled !== null && table.minimumClaimScore > 0 ? legalClaims.filter((c) => c.score < rolled) : [];
  const honestClaims = rolled !== null && table.minimumClaimScore > 0 ? legalClaims.filter((c) => c.score >= rolled) : legalClaims;

  const derived: IEntityClientTableDerived = {
    challengerId,
    isChallenger: me !== null && me === challengerId,
    isActiveRoller,
    blockingOpponentId: resolveBlockingSuspendedOpponentId(table.phase, table.activeRollerId, challengerId, me, table.suspendedPlayerIds),
    cupAction: cupActionOf(table, isActiveRoller),
    legalClaims,
    tacticalClaims,
    honestClaims,
    rolledLabel: rolled === null ? null : scoreLabel(rolled),
    opponentClaimDice: table.opponentClaim === null ? null : scoreToDice(table.opponentClaim.score),
    canBelieve: table.opponentClaim !== null && table.opponentClaim.score !== MEYER_RANK,
    revealLabels: table.revealResult === null
      ? null
      : { claimed: scoreLabel(table.revealResult.claimedScore), actual: scoreLabel(table.revealResult.trueScore) },
  };
  return derived;
}

/** What a tap on the cup does: the roller rolls, then peeks once; anything else is no move. */
function cupActionOf(table: IStateClientTable, isActiveRoller: boolean): IEntityClientTableDerived['cupAction'] {
  if (!isActiveRoller || table.activeRoundId === null) return null;
  if (table.phase === GamePhase.ROLLING) return 'roll';
  if (table.phase === GamePhase.PEEKING && !table.diceRevealed) return 'peek';
  return null;
}
